import classNames from "classnames"
import Image from "next/image"
import React from "react"

import { Analytics } from "@bounce/analytics"
import { Trans, useBounceGlobalValues, useTranslation } from "@bounce/i18n"
import { App } from "@bounce/util"
import { Button, isApple, Separator, useIsClient } from "@bounce/web-components"

import { useAppStoreUrl } from "../../../../hooks/useAppStoreUrl"
import type { GenericInterpolations } from "../../../../hooks/useGenericInterpolations"
import { InteractionEvents } from "../../../../libs/analytics/events"
import AppStore from "../assets/app-store.svg?url"
import PlayStore from "../assets/play-store.svg?url"

import HeroImageDesktop from "../assets/lady-phone-desktop.png"
import HeroImageMobile from "../assets/lady-phone-mobile.png"
import Underline from "../assets/underline.svg"

type HeroProps = {
  interpolations: GenericInterpolations
  className?: string
}

const AppStoreComponent = ({ isApple }: { isApple: boolean }) => {
  const { t } = useTranslation("marketing-ls-app")
  const bounceGlobals = useBounceGlobalValues()
  const isClient = useIsClient()

  const altText = isApple
    ? t("screen.appDownloadScreen.hero.ratingAppleAlt", "Download on the App Store")
    : t("screen.appDownloadScreen.hero.ratingGoogleAlt", "Get it on Google Play")

  const trackStoreImageClick = () => {
    Analytics.track(InteractionEvents.PressedAppStoresImages, { isApple })
  }

  if (!isClient) {
    return null
  }

  return (
    <div onClick={trackStoreImageClick} className="flex w-full items-center justify-center md:justify-start">
      <Image alt={altText} src={isApple ? AppStore : PlayStore} height={26} width={102} />
      <Separator direction="vertical" className="mx-3 h-[18px]" />
      <p className="label2 leading-none text-white">
        {t("screen.appDownloadScreen.hero.ratingText", {
          defaultValue: "Rated {{appRating}}/5",
          appRating: bounceGlobals.appRating,
        })}
      </p>
    </div>
  )
}

export const Hero = ({ interpolations, className }: HeroProps) => {
  const { t } = useTranslation("marketing-ls-app")
  const { PRICE } = interpolations
  const isAppStore = isApple()
  const { onClick, href } = useAppStoreUrl({
    app: App.Customer,
    eventProps: { location: "AppDownloadHero", cta: "AppDownloadHero" },
  })

  return (
    <section className={classNames("relative mb-[130px] w-full overflow-x-clip bg-primary-700", className)}>
      <Image alt="" src={HeroImageMobile} className="md:hidden" priority />
      <div className="mx-auto max-w-screen-xl px-6 md:grid md:grid-cols-2 md:items-stretch">
        <div className="z-1 pb-16 pt-8 md:py-section lg:max-w-[600px]">
          <h1 className="title1 mb-4 text-white">
            {t("screen.appDownloadScreen.hero.title", "Download the Bounce luggage storage app")}
          </h1>
          <p className="subtitle mb-14 text-white md:w-3/4">
            <Trans
              ns="marketing-ls-app"
              i18nKey="screen.appDownloadScreen.hero.subtitleV2"
              values={{ luggageStoragePrice: PRICE }}
              components={{
                span: <span className="relative" />,
                underline: <Underline className="absolute right-0 mt-2" />,
              }}
              defaults="The easiest way to store your stuff all over the world from just <span> {{ luggageStoragePrice }}/day.<underline /></span>"
            />
          </p>
          <Button
            onClick={onClick}
            href={href}
            rel="nofollow"
            color="white"
            className="label2 mb-6 h-8 w-full py-5 !text-2xl !leading-7 xs:label1 xs:h-auto md:w-min">
            {t("screen.appDownloadScreen.hero.cta", "Download app")}
          </Button>
          <AppStoreComponent isApple={isAppStore} />
        </div>
        <div className="z-0 hidden md:flex">
          <Image
            alt=""
            src={HeroImageDesktop}
            className={classNames(
              "hidden md:inline",
              "absolute inset-y-0 left-[40%] -z-1",
              "h-full w-auto object-cover",
            )}
          />
        </div>
      </div>
    </section>
  )
}
